import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'
import { useEffect, useRef } from 'react'
import { useFamilyAddedProductTypesQuery } from '../api/useFamilyAddedProductTypesQuery'
import { useNodeMapActions } from '../features/keyOperations'
import { useShallowSelector } from '../Workspace2Store'

/**
 * Properties for the useFamilyAddedProductTypes hook.
 */
interface UseFamilyAddedProductTypesProps {
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    projectId: string
    setFamilyAddedProductTypes?: (
        familyAddedProductTypes: ProjectElementNodeWithDrawingDesignation[]
    ) => void
}

/**
 * Custom hook to manage fetching family added product types based on the selected project element node.
 * @param {string} param0.projectId - The ID of the project for which to fetch family added product types.
 * @param {Function} param0.setFamilyAddedProductTypes - A callback function to set the family added product types in the state.
 * @returns {Object} An object containing the `fetchFamilyAddedProductTypes` function to trigger the data fetch.
 */
export const useFamilyAddedProductTypes = ({
    selectedProjectElementNode,
    projectId,
}: UseFamilyAddedProductTypesProps) => {
    const {
        setSelectedProjectElementNode,
        setFamilyAddedProductTypes,
        setTextContentElementToAdd,
    } = useShallowSelector((s) => ({
        setSelectedProjectElementNode: s.actions.setSelectedProjectElementNode,
        setFamilyAddedProductTypes: s.actions.setFamilyAddedProductTypes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
    }))
    const { setSaveOperationInProgress } = useNodeMapActions()
    const { fetchFamilyAddedProductTypes } = useFamilyAddedProductTypesQuery({
        projectId,
        setFamilyAddedProductTypes,
    })
    const previousNodeRef = useRef(selectedProjectElementNode)

    /**
     * Runs on every Family/Section/Product Type selection change: fetches added
     * product types for a Family (or clears them otherwise) and syncs the
     * selection into the shared store.
     */
    useEffect(() => {
        const node = selectedProjectElementNode
        const previousNode = previousNodeRef.current
        if (!node) return
        previousNodeRef.current = node

        // Reset stuck edit/add flags on a real Family/Section switch, not the add-to-project id swap (same treePath).
        const shouldResetContentEditFlags =
            !!previousNode && previousNode.treePath !== node.treePath
        if (shouldResetContentEditFlags) {
            setTextContentElementToAdd(null)
            setSaveOperationInProgress(false)
        }

        const isProductFamily =
            node?.elementType === ProjectElementType.PRODUCTFAMILY
        const hasRequiredIds = node?.elementId && node?.id

        if (isProductFamily && hasRequiredIds) {
            fetchFamilyAddedProductTypes(
                selectedProjectElementNode.elementId ?? '',
                selectedProjectElementNode.id ?? ''
            )
        } else {
            setFamilyAddedProductTypes?.([])
        }
        setSelectedProjectElementNode(selectedProjectElementNode)
    }, [
        selectedProjectElementNode,
        projectId,
        fetchFamilyAddedProductTypes,
        setFamilyAddedProductTypes,
        setSelectedProjectElementNode,
        setTextContentElementToAdd,
        setSaveOperationInProgress,
    ])

    return {
        fetchFamilyAddedProductTypes,
    }
}
